import { useState, useEffect, useCallback } from 'react';
import { usePharma } from '../../../providers/PharmaProvider';
import { CombinedApiData } from '../interfaces/participantTrackerTypes';
import type { SptDataSource } from '../types';

const API_URL = 'https://dev.seevividly.com/api_clinical_trials/get_clinical_trial_overview';

export const useParticipantOverviewData = (sptSource: SptDataSource = 'production') => {
  const { user } = usePharma();
  const [data, setData] = useState<CombinedApiData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => { // Wrap fetchData in useCallback
    if (!user?.user_name || !user?.p) {
      setData([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          u: user.user_name,
          p: user.p,
          ...(sptSource !== 'production' && { spt_source: sptSource }),
        }),
      });

      const responseText = await response.text();
      if (!response.ok) {
        console.error("Participant Overview API Error Response:");
        throw new Error(`API request failed: ${response.status} ${response.statusText || ''}.`);
      }

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError: any) {
        console.error("Failed to parse Participant Overview API response:");
        throw new Error(`Invalid JSON received from API. ${parseError.message}`);
      }

      if (result && result.status === 'success' && Array.isArray(result.data)) {
        setData(result.data as CombinedApiData[]);
      } else {
        console.error('Unexpected Participant Overview API response format:');
        setError('Received invalid data format from API.');
        setData([]);
      }
    } catch (err: any) {
      console.error('Fetch participant overview error:');
      setError(err.message || 'Failed to load participant data.');
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [user, sptSource]);

  useEffect(() => {
    fetchData();
  }, [fetchData]); // Depend on fetchData

  return { data, isLoading, error, refetch: fetchData };
};
